import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Flask, Sparkle, CheckSquare, Square, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";

const PHASES = [
    { v: "", label: "—" },
    { v: "preparacion", label: "Preparación" },
    { v: "recuperacion", label: "Recuperación inmediata" },
    { v: "mantenimiento", label: "Mantenimiento" },
];

const OBJECTIVE_SUGGESTIONS = [
    "Antiinflamatorio", "Antioxidante", "Recuperación de la piel",
    "Síntesis de colágeno", "Relajante muscular", "Sueño y descanso",
    "Estrés y cortisol", "Energía y mitocondrial", "Salud articular",
    "Inmunidad", "Antienvejecimiento / NAD+", "Cabello y uñas",
    "Cicatrización post-procedimiento", "Hidratación cutánea",
    "Detox hepático", "Sensibilidad a la insulina",
];

export default function FormulaGenerator() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [compounds, setCompounds] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [patients, setPatients] = useState([]);

    const [mode, setMode] = useState("auto"); // auto | validate
    const [objective, setObjective] = useState("Antiinflamatorio");
    const [population, setPopulation] = useState("adultos sanos 20-90 años");
    const [aesthetic, setAesthetic] = useState("");
    const [phase, setPhase] = useState("");
    const [patientId, setPatientId] = useState(searchParams.get("patient") || "");
    const parentId = searchParams.get("parent") || null;
    const presetPref = (searchParams.get("preferred") || "").split(",").filter(Boolean);
    const [preferred, setPreferred] = useState(presetPref);
    const [excluded, setExcluded] = useState([]);
    const [notes, setNotes] = useState("");
    const [customPrompt, setCustomPrompt] = useState("");
    const [preferMaxDose, setPreferMaxDose] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/compounds").then((r) => setCompounds(r.data));
        api.get("/treatments").then((r) => setTreatments(r.data));
        api.get("/patients").then((r) => setPatients(r.data));
    }, []);

    const togglePreferred = (slug) =>
        setPreferred((p) => p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]);
    const toggleExcluded = (slug) =>
        setExcluded((p) => p.includes(slug) ? p.filter((x) => x !== slug) : [...p, slug]);

    const submit = async () => {
        if (mode === "validate" && preferred.length < 2) {
            toast.error("En modo Validar combinación selecciona al menos 2 principios activos en 'Preferidos'");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/formulas/generate", {
                mode,
                objective,
                target_population: population,
                aesthetic_treatment: aesthetic || null,
                treatment_phase: phase || null,
                preferred_compound_slugs: preferred,
                excluded_compound_slugs: excluded,
                notes: notes || null,
                custom_prompt: customPrompt || null,
                prefer_max_dose: preferMaxDose,
                patient_id: patientId || null,
                parent_formula_id: parentId || null,
            });
            if (data.status === "generating") {
                toast.info("Generando fórmula — Claude está pensando…");
            } else if (data.feasibility === "rejected") {
                toast.warning("La combinación propuesta NO es recomendable — revisa los motivos en el detalle");
            } else {
                toast.success("Fórmula generada con éxito");
            }
            navigate(`/formulas/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error generando la fórmula");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow="Diseño de fórmula · Claude Sonnet 4.5"
                title={<>Generar fórmula <em className="text-[var(--sage)]">{mode === "validate" ? "validando" : "automáticamente"}</em></>}
                lead="Define objetivo, paciente y restricciones. En modo Validar combinación, el sistema evalúa la viabilidad de los activos que tú elijas. Las dosis tienden a los rangos terapéuticos altos dentro del UL seguro y la fórmula busca ser DIFERENCIAL respecto a productos comerciales."
            />

            <div className="px-8 md:px-12 py-8 max-w-6xl">
                {/* Mode switch */}
                <div className="border hairline bg-white p-2 flex gap-2 mb-8 w-fit" data-testid="generator-mode-switch">
                    <button
                        type="button"
                        onClick={() => setMode("auto")}
                        className={`px-5 py-2.5 text-sm transition-colors ${mode === "auto" ? "bg-[var(--sage)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--cream)]"}`}
                        data-testid="generator-mode-auto"
                    >🧠 Generación automática</button>
                    <button
                        type="button"
                        onClick={() => setMode("validate")}
                        className={`px-5 py-2.5 text-sm transition-colors ${mode === "validate" ? "bg-[var(--sage)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--cream)]"}`}
                        data-testid="generator-mode-validate"
                    >🧪 Validar mi combinación</button>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <Field label="Objetivo terapéutico (texto libre)">
                            <input
                                value={objective}
                                onChange={(e) => setObjective(e.target.value)}
                                className="i"
                                placeholder="ej. mejora elasticidad cutánea post-láser, dolor articular crónico…"
                                list="objective-suggestions"
                                data-testid="generator-objective-input"
                            />
                            <datalist id="objective-suggestions">
                                {OBJECTIVE_SUGGESTIONS.map((o) => <option key={o} value={o}/>)}
                            </datalist>
                        </Field>

                        <Field label="Paciente (opcional)">
                            <select value={patientId} onChange={(e)=>setPatientId(e.target.value)} className="i" data-testid="generator-patient-select">
                                <option value="">— Sin paciente asociado —</option>
                                {patients.map((p) => <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ""}</option>)}
                            </select>
                        </Field>

                        <Field label="Población diana">
                            <input value={population} onChange={(e) => setPopulation(e.target.value)} className="i" data-testid="generator-population-input"/>
                        </Field>

                        <Field label="Tratamiento médico-estético (opcional)">
                            <select value={aesthetic} onChange={(e) => setAesthetic(e.target.value)} className="i" data-testid="generator-treatment-select">
                                <option value="">— Ninguno —</option>
                                {treatments.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
                            </select>
                        </Field>

                        {aesthetic && (
                            <Field label="Fase del protocolo">
                                <select value={phase} onChange={(e) => setPhase(e.target.value)} className="i" data-testid="generator-phase-select">
                                    {PHASES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                                </select>
                            </Field>
                        )}

                        <Field label="Instrucciones adicionales (prompt libre para el formulador)">
                            <textarea
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                rows={4}
                                className="i resize-none"
                                placeholder="Ej: priorizar absorción en mucosa intestinal, evitar excipientes de origen animal, dosis nocturna…"
                                data-testid="generator-customprompt-input"
                            />
                        </Field>

                        <Field label="Notas internas (no se envían al modelo)">
                            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="i" data-testid="generator-notes-input"/>
                        </Field>

                        <label className="flex items-center gap-3 p-4 border hairline bg-white cursor-pointer" data-testid="generator-maxdose-toggle">
                            <input type="checkbox" checked={preferMaxDose} onChange={(e)=>setPreferMaxDose(e.target.checked)} className="w-4 h-4"/>
                            <div>
                                <div className="text-sm font-medium">Apuntar a dosis altas dentro del UL seguro</div>
                                <div className="text-xs text-[var(--ink-2)]">Máxima eficacia clínica respetando límites toxicológicos</div>
                            </div>
                        </label>
                    </div>

                    <div className="space-y-6">
                        <Field label={`${mode === "validate" ? "Principios activos a validar (mínimo 2)" : "Principios preferidos (opcional)"} · ${preferred.length} seleccionados`}>
                            {mode === "auto" && preferred.length === 0 && (
                                <p className="text-xs text-[var(--ink-2)] mb-2 italic" data-testid="generator-auto-hint">
                                    Sin selección → la IA elegirá los principios activos más apropiados del catálogo para cumplir el objetivo.
                                </p>
                            )}
                            <ChipList items={compounds} selected={preferred} onToggle={togglePreferred} testid="generator-preferred"/>
                        </Field>

                        {mode === "auto" && (
                            <Field label={`Principios excluidos · ${excluded.length} seleccionados`}>
                                <ChipList items={compounds} selected={excluded} onToggle={toggleExcluded} testid="generator-excluded" variant="exclude"/>
                            </Field>
                        )}

                        {mode === "validate" && (
                            <div className="p-4 border hairline bg-[var(--cream)] text-sm leading-relaxed flex gap-3" data-testid="generator-validate-info">
                                <ShieldWarning size={20} className="text-[var(--terra-2)] flex-shrink-0 mt-0.5" weight="duotone"/>
                                <div>
                                    <div className="font-medium mb-1">Modo Validar combinación</div>
                                    <p className="text-[var(--ink-2)]">Claude evaluará si los activos elegidos son seguros y sinérgicos. Si NO lo son, devolverá un veredicto de <strong>rechazo</strong> con motivos clínicos detallados.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-10 flex items-center justify-between border-t hairline pt-6 flex-wrap gap-3">
                    <div className="text-xs text-[var(--ink-3)] mono max-w-md">
                        Cada componente citará external_id (PMID/PMC/NCT) trazable y se buscarán productos comerciales con composición similar.
                    </div>
                    <button onClick={submit} disabled={submitting || !objective.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="generator-submit-button">
                        <Sparkle size={16} weight="bold"/>
                        {submitting ? "Sintetizando evidencia…" : (mode === "validate" ? "Validar combinación" : "Generar fórmula")}
                    </button>
                </div>
            </div>
            <style>{`.i{width:100%;padding:10px 12px;border:1px solid var(--line);background:#fff;font-size:.875rem;outline:none;transition:border-color .15s ease}.i:focus{border-color:var(--sage)}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (<div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>);
}

function ChipList({ items, selected, onToggle, variant, testid }) {
    const [filter, setFilter] = useState("");
    const filtered = items.filter((c) => !filter || c.name.toLowerCase().includes(filter.toLowerCase()));
    return (
        <div className="border hairline bg-white" data-testid={testid}>
            <input
                value={filter}
                onChange={(e)=>setFilter(e.target.value)}
                placeholder={`Filtrar entre ${items.length} compuestos…`}
                className="w-full px-3 py-2 border-b hairline text-xs outline-none"
                data-testid={`${testid}-filter`}
            />
            <div className="max-h-72 overflow-y-auto p-3 flex flex-wrap gap-2">
                {filtered.map((c) => {
                    const active = selected.includes(c.slug);
                    const cls = active
                        ? variant === "exclude" ? "bg-[var(--terra)] text-white border-[var(--terra)]" : "bg-[var(--sage)] text-white border-[var(--sage)]"
                        : "bg-white text-[var(--ink-2)] hover:bg-[var(--cream)]";
                    return (
                        <button
                            key={c.slug}
                            type="button"
                            onClick={() => onToggle(c.slug)}
                            className={`text-xs px-3 py-1.5 border hairline transition-colors ${cls}`}
                            data-testid={`${testid}-${c.slug}`}
                        >{c.name}</button>
                    );
                })}
                {filtered.length === 0 && <div className="text-xs text-[var(--ink-3)] py-2 mono">Sin coincidencias</div>}
            </div>
        </div>
    );
}
